import {getEnumByValue, LooseAutocomplete} from "@/tools";
import {
    DomainExportSection,
    DomainExportYaml,
    ExtensionExport,
    FaxExport,
    GatewayExport,
    GroupExport,
    IvrExport,
    IvrMediaExport,
    LocationExport,
    PhoneButton,
} from "../export/types";
import {parse} from "yaml";
import {useCreateDomainMutation, CreateDomainMutationHookResult} from "../create/mutations.generated";
import {
    useUpdateDomainSettingsMutation,
    UpdateDomainSettingsMutationHookResult,
} from "@/components/domain_settings/settings/mutations.generated";
import {
    AddressBookEntryData,
    AllocationInput,
    CallGroupType,
    ClientType,
    CreateCallGroupMember,
    CreateUserSpeedDial,
    DeviceTypeSet,
    Error,
    GroupType,
    IvrActionType,
    MutationResult,
    PrivateRoutingType,
    SaveResult,
    ServicesAllocationInput,
    UpdateConnectionPrompt,
    UpdateIvrButton,
    UpdateIvrMenuMedia,
} from "@/api";
import {useSaveLocationMutation, SaveLocationMutationHookResult} from "@/components/locations/queries.generated";
import {
    useDomainAllocationUpdateMutation,
    useDomainQuotaDefaultsUpdateMutation,
    DomainAllocationUpdateMutationHookResult,
    DomainQuotaDefaultsUpdateMutationHookResult,
    DomainAllocationUpdateMutationVariables,
} from "@/components/domain_settings/mutations.generated";
import {
    useCreateExtensionMutation,
    CreateExtensionMutationHookResult,
} from "@/components/extension/create/queries.generated";
import {
    useLocationIdLazyQuery,
    LocationIdLazyQueryHookResult,
    useIvrDataLazyQuery,
    IvrDataLazyQueryHookResult,
    GroupNumberIdLazyQueryHookResult,
    useGroupNumberIdLazyQuery,
    DomainImportDeviceVariantListLazyQueryHookResult,
    useDomainImportDeviceVariantListLazyQuery,
    AddDomainAdminsMutationHookResult,
    useAddDomainAdminsMutation,
    SystemDeviceTypeSetsLazyQueryHookResult,
    useSystemDeviceTypeSetsLazyQuery,
} from "./queries.generated";
import {useCreateIvrMutation, CreateIvrMutationHookResult} from "@/components/ivr/list/create/queries.generated";
import {
    useUpdateDomainE164RangesMutation,
    UpdateDomainE164RangesMutationHookResult,
} from "@/components/domain_settings/allocated-e164-ranges/mutations.generated";
import {
    useUpdateIvrMutation,
    UpdateIvrMutationHookResult,
    IvrNumberIdLazyQueryHookResult,
    useIvrNumberIdLazyQuery,
} from "@/components/ivr/queries.generated";
import {
    useCreateFaxMutation,
    CreateFaxMutationHookResult,
    useUpdateFaxMutation,
    UpdateFaxMutationHookResult,
} from "@/components/fax/mutations.generated";
import {
    useUpdateGatewayMutation,
    UpdateGatewayMutationHookResult,
} from "@/components/gateway/update/mutations.generated";
import {
    useSavePickupGroupMutation,
    SavePickupGroupMutationHookResult,
} from "@/components/pickup-group/queries.generated";
import {useSaveGroupMembershipMutation, SaveGroupMembershipMutationHookResult} from "@/mutations.generated";
import {
    useCreateCallGroupMutation,
    CreateCallGroupMutationHookResult,
    useUpdateCallGroupMutation,
    UpdateCallGroupMutationHookResult,
} from "@/components/call-group/queries.generated";
import {FetchResult} from "@apollo/client";
import {
    useUpdateCallQueueMutation,
    UpdateCallQueueMutationHookResult,
} from "@/components/call-group/call-queue/mutation.generated";
import zip from "lodash/zip";
import {
    useUpdateIvrProfileMutation,
    UpdateIvrProfileMutationHookResult,
    ActivateIvrProfileMutationHookResult,
    useActivateIvrProfileMutation,
    UpdateIvrProfileMutationVariables,
} from "@/components/ivr/profiles/mutation.generated";
import {
    useUpdateExtensionLimitMutation,
    UpdateExtensionLimitMutationHookResult,
    ExtensionNumberIdLazyQueryHookResult,
    useExtensionNumberIdLazyQuery,
} from "@/components/extension/limit-settings/queries.generated";
import {
    useSaveClientDeviceMutation,
    SaveClientDeviceMutationHookResult,
    useSaveThirdPartyDeviceMutation,
    SaveThirdPartyDeviceMutationHookResult,
    useSaveProvisionedDeviceMutation,
    SaveProvisionedDeviceMutationHookResult,
    useSavePhoneButtonsMutation,
    SavePhoneButtonsMutationHookResult,
} from "@/components/extension/devices/queries.generated";
import {CreateMutation, Stage, StageID, stages, StepID, steps, UpdateMutation} from "./utils";
import {HookClass} from "@dvrt/graphql-widgets";
import {
    UpdateGatewayRoutingMutationHookResult,
    useUpdateGatewayRoutingMutation,
} from "@/components/gateway/routing/mutations.generated";
import {CreateGatewayMutationHookResult, useCreateGatewayMutation} from "@/components/gateway/queries.generated";
import {IntlShape, MessageDescriptor, useIntl} from "react-intl";
import {useDispatch} from "react-redux";
import {StructureConfig, useStructureConfig} from "@/context/StructureConfigProvider";
import {useState} from "react";
import {
    DestinationLazyQueryHookResult,
    useDestinationLazyQuery,
} from "@/components/private-routing-destination/queries.generated";
import {VendorsLazyQueryHookResult, useVendorsLazyQuery} from "@/queries.generated";
import {
    SaveDeviceVariantMutationHookResult,
    useSaveDeviceVariantMutation,
} from "@/components/device-variant/queries.generated";
import {resetCacheAfterEveryMutationOptions} from "@/apollo-client/factory";
import {
    AddAddressBookEntryMutationHookResult,
    useAddAddressBookEntryMutation,
} from "@/components/address-book/queries.generated";
import {parseContext} from "@/utils/ErrorHelper";
import {DomainImportBenchmarker} from "./benchmark";
import {SectionBenchmarker} from "@/utils/benchmark";

// we do have a snake to camel case in the @/tools, but that for some reason capitalizes the first letter
// which we do not want here, so we implement the 'correct' version here
const snakeToCamelCase = (word: string): string => word.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

/**
 * To be backward compatible with now removed "NONE" private routing type,
 * we map it to "PSTN_ONLY" here.
 * */
const handlePrivateRoutingType = (value: PrivateRoutingType): PrivateRoutingType => {
    if (value?.toLowerCase() === "none") return PrivateRoutingType.PstnOnly;
    return value;
};

const SERVICE_ALLOCATION_KEY_MAP = {
    ivr: "cc_aattendant",
    call_queue: "cc_queue",
} as const;

const getDTSWithLinkedDevice = (deviceTypeSets: DeviceTypeSet[]): string[] =>
    deviceTypeSets.filter((dts) => dts.devices.includes("linked")).map((dts) => dts.name);

/** Return null if a string is empty. Used on fields with validator for min length. */
const nullIfEmpty = (value: string | null | undefined): string | null => {
    if (!value || value.trim() === "") return null;
    return value;
};

type _MutationErrorLocations =
    | "domain_allocations"
    | "domain_defaults"
    | "domain_settings"
    | "domain_e164_ranges"
    | "create"
    | "update"
    | "limit"
    | "profile"
    | "profile_activation"
    | "profile_rules"
    | "devices_client"
    | "devices_third_party"
    | "devices_provisioned"
    | "devices_buttons"
    | "domain_admins"
    | "domain_address_book";

type MutationErrorLocations = LooseAutocomplete<_MutationErrorLocations>[];
type CurrentStageErrors = {stage: string; errors: Error[]};

/**
 * Type used only to avoid linting error.
 * But is also valid for the variables it is used for.
 **/
interface Item {
    number: {
        shortNumber: string;
        id: number;
    };
}

// TODO: refactor this whole file when needed
/**
 * Mutator class for handling all the data, mutations and errors
 * for importing a domain.
 **/
export default class Mutator extends HookClass {
    /** Errors that occur during the mutations.
     * These errors get displayed if there are any, and
     * stops the process.
     **/
    private mutationErrors: Record<string, Error[]> = {};

    private intl: IntlShape;
    private structureConfig: StructureConfig;
    private dispatch: any;
    private benchmarker: DomainImportBenchmarker;

    private successNotification: (id: number, name: string) => any;

    data: DomainExportYaml | undefined;
    setData: (data: DomainExportYaml) => void;

    // exposing for error message card
    domainId: number | undefined;
    setDomainId: (id: number) => void;

    // Mappings of the old and the new values.
    // (old: file | new: after the mutation)
    private extensionIds: Record<number, number> = {};
    private extensionNumberIdToId: Record<number, number> = {};
    private locationIds: Record<string, number> = {};
    private numberIds: Record<string, number> = {};
    private groupIds: Record<string, number> = {};
    private gatewayIds: Record<number, number> = {};
    private destinationNames: Record<string, number> = {}; // name -> id
    private vendorNames: Record<string, number> = {}; // name -> id
    private deviceVariants: Set<string>; // set of all global device variants

    // All the mutations that are used.
    private createDomainMutation: CreateDomainMutationHookResult[0];
    private updateDomainSettingsMutation: UpdateDomainSettingsMutationHookResult[0];
    private updateDomainAllocationMutation: DomainAllocationUpdateMutationHookResult[0];
    private updateDomainDefaultMutation: DomainQuotaDefaultsUpdateMutationHookResult[0];
    private updateDomainE164RangesMutation: UpdateDomainE164RangesMutationHookResult[0];
    private createLocationMutation: SaveLocationMutationHookResult[0];
    private createExtensionMutation: CreateExtensionMutationHookResult[0];
    private updateExtensionLimitMutation: UpdateExtensionLimitMutationHookResult[0];
    private saveClientDeviceMutation: SaveClientDeviceMutationHookResult[0];
    private saveThirdPartyDeviceMutation: SaveThirdPartyDeviceMutationHookResult[0];
    private saveProvisionedDeviceMutation: SaveProvisionedDeviceMutationHookResult[0];
    private savePhoneButtonsMutation: SavePhoneButtonsMutationHookResult[0];
    private createIvrMutation: CreateIvrMutationHookResult[0];
    private updateIvrMutation: UpdateIvrMutationHookResult[0];
    private updateIvrProfileMutation: UpdateIvrProfileMutationHookResult[0];
    private activateIvrProfileMutation: ActivateIvrProfileMutationHookResult[0];
    private createFaxMutation: CreateFaxMutationHookResult[0];
    private updateFaxMutation: UpdateFaxMutationHookResult[0];
    private createGatewayMutation: CreateGatewayMutationHookResult[0];
    private updateGatewayMutation: UpdateGatewayMutationHookResult[0];
    private updateGatewayRoutingMutation: UpdateGatewayRoutingMutationHookResult[0];
    private savePickupGroupMutation: SavePickupGroupMutationHookResult[0];
    private createCallGroupMutation: CreateCallGroupMutationHookResult[0];
    private updateCallGroupMutation: UpdateCallGroupMutationHookResult[0];
    private updateCallQueueMutation: UpdateCallQueueMutationHookResult[0];
    private saveGroupMembershipMutation: SaveGroupMembershipMutationHookResult[0];
    private createDeviceVariantMutation: SaveDeviceVariantMutationHookResult[0];
    private addDomainAdminsMutation: AddDomainAdminsMutationHookResult[0];
    private addAddressBookEntry: AddAddressBookEntryMutationHookResult[0];

    // All the queries
    private locationIdQuery: LocationIdLazyQueryHookResult[0];
    private extensionNumberIdQuery: ExtensionNumberIdLazyQueryHookResult[0];
    private ivrNumberIdQuery: IvrNumberIdLazyQueryHookResult[0];
    private callGroupNumberIdQuery: GroupNumberIdLazyQueryHookResult[0];
    private ivrDataQuery: IvrDataLazyQueryHookResult[0];
    private destinationsQuery: DestinationLazyQueryHookResult[0];
    private vendorsQuery: VendorsLazyQueryHookResult[0];
    private deviceVariantsQuery: DomainImportDeviceVariantListLazyQueryHookResult[0];
    private deviceTypeSetsQuery: SystemDeviceTypeSetsLazyQueryHookResult[0];

    // The current stage.
    private _state: string | undefined;
    private setState: (state: string) => void;

    // Trying to keep track of the progress.
    // It is not 100% accurate, but at least the user
    // knows something is happening.
    steps: number = stages.length;
    currentStep: number;
    private setCurrentStep: (step: number) => void;

    cancelled: boolean;
    private setCancelled: (cancelled: boolean) => void;
    readonly cancel = (): void => {
        this.setCancelled(true);
    };

    // Media file management
    private getMediaFile: (mediaId: string) => Promise<File>;

    constructor() {
        super();
        this.benchmarker = new SectionBenchmarker();
    }

    useHooks(
        getMediaFile: (mediaId: string) => Promise<File>,
        successNotification: (id: number, name: string) => any,
    ): void {
        this.getMediaFile = getMediaFile;
        this.intl = useIntl();
        this.structureConfig = useStructureConfig();
        this.dispatch = useDispatch();
        this.successNotification = successNotification;

        [this.cancelled, this.setCancelled] = useState<boolean>(false);

        [this.data, this.setData] = useState<DomainExportYaml>();
        [this._state, this.setState] = useState<string>();
        [this.currentStep, this.setCurrentStep] = useState<number>(0);

        [this.domainId, this.setDomainId] = useState<number>();

        [this.createDomainMutation] = useCreateDomainMutation();
        [this.updateDomainSettingsMutation] = useUpdateDomainSettingsMutation();
        [this.updateDomainAllocationMutation] = useDomainAllocationUpdateMutation();
        [this.updateDomainDefaultMutation] = useDomainQuotaDefaultsUpdateMutation();
        [this.updateDomainE164RangesMutation] = useUpdateDomainE164RangesMutation();
        [this.createLocationMutation] = useSaveLocationMutation();
        [this.createExtensionMutation] = useCreateExtensionMutation();
        [this.updateExtensionLimitMutation] = useUpdateExtensionLimitMutation();
        [this.saveClientDeviceMutation] = useSaveClientDeviceMutation();
        [this.saveThirdPartyDeviceMutation] = useSaveThirdPartyDeviceMutation();
        [this.saveProvisionedDeviceMutation] = useSaveProvisionedDeviceMutation();
        [this.savePhoneButtonsMutation] = useSavePhoneButtonsMutation();
        [this.createIvrMutation] = useCreateIvrMutation();
        [this.updateIvrMutation] = useUpdateIvrMutation();
        [this.updateIvrProfileMutation] = useUpdateIvrProfileMutation();
        [this.activateIvrProfileMutation] = useActivateIvrProfileMutation();
        [this.createFaxMutation] = useCreateFaxMutation();
        [this.updateFaxMutation] = useUpdateFaxMutation();
        [this.createGatewayMutation] = useCreateGatewayMutation();
        [this.updateGatewayMutation] = useUpdateGatewayMutation();
        [this.updateGatewayRoutingMutation] = useUpdateGatewayRoutingMutation();
        [this.savePickupGroupMutation] = useSavePickupGroupMutation();
        [this.createCallGroupMutation] = useCreateCallGroupMutation();
        [this.updateCallGroupMutation] = useUpdateCallGroupMutation();
        [this.updateCallQueueMutation] = useUpdateCallQueueMutation();
        [this.saveGroupMembershipMutation] = useSaveGroupMembershipMutation();
        [this.createDeviceVariantMutation] = useSaveDeviceVariantMutation();
        [this.addDomainAdminsMutation] = useAddDomainAdminsMutation();
        [this.addAddressBookEntry] = useAddAddressBookEntryMutation();

        [this.locationIdQuery] = useLocationIdLazyQuery();
        [this.extensionNumberIdQuery] = useExtensionNumberIdLazyQuery();
        [this.ivrNumberIdQuery] = useIvrNumberIdLazyQuery();
        [this.callGroupNumberIdQuery] = useGroupNumberIdLazyQuery();
        [this.ivrDataQuery] = useIvrDataLazyQuery();
        [this.destinationsQuery] = useDestinationLazyQuery();
        [this.vendorsQuery] = useVendorsLazyQuery();
        [this.deviceVariantsQuery] = useDomainImportDeviceVariantListLazyQuery();
        [this.deviceTypeSetsQuery] = useSystemDeviceTypeSetsLazyQuery();
    }

    reset(): void {
        resetCacheAfterEveryMutationOptions.reset = true;

        this.setData(undefined);
        this.setState(undefined);

        this.setCurrentStep(0);

        this.extensionIds = {};
        this.extensionNumberIdToId = {};
        this.locationIds = {};
        this.numberIds = {};
        this.groupIds = {};
        this.gatewayIds = {};
        this.destinationNames = {};
        this.vendorNames = {};
        this.deviceVariants = new Set();

        this.setCancelled(false);

        Object.keys(this.mutationErrors).forEach((key) => {
            delete this.mutationErrors[key];
        });
    }

    /** Get the current progress in percent */
    get progress(): number {
        return (this.currentStep / this.steps) * 100;
    }

    /** Translated current state */
    get state(): string | undefined {
        return this._state;
    }

    get foreshadowedStates(): Stage[] {
        return stages.slice(this.currentStep - 1, this.currentStep + 2);
    }

    private getCurrentNumberId(oldNumberId: number | undefined): number | undefined {
        if (oldNumberId === undefined) return undefined;

        const {users, groups, faxes, ivrs} = this.dataCopy;

        const endpoint = (users as Item[])
            .concat(groups as Item[], faxes as Item[], ivrs as Item[])
            .filter((item) => item.number.shortNumber)
            .find((item) => item.number.id === oldNumberId);

        let result = undefined;

        if (endpoint) result = this.numberIds[endpoint.number.shortNumber];
        if (!result) console.error("New number id not found", oldNumberId);

        return result;
    }

    /**
     * Resolves a mutation promise and handles errors.
     * The reason why it does not accept the mutation and the variables separately is due to
     * bad type inference. If we do that, the variables passed will be prioritized over the
     * actual variables the mutation expects, therefore losing all type safety regarding the variables.
     * To avoid that, we just accept the 'constructed-wyth-type-safety' promise directly, and resolve it.
     *
     * @param mutationPromise the mutation promise to resolve.
     * @param errorIdentifier used to identify the error in the error list.
     **/
    private async resolveMutation(
        mutationPromise: Promise<FetchResult<any>>,
        errorIdentifier: MutationErrorLocations,
    ): Promise<number | void | undefined> {
        const {data} = await mutationPromise;
        const firstKey = Object.keys(data)[0];
        const firstData = data[firstKey] as MutationResult;
        if (firstData?.errors?.length > 0) {
            console.info("mutation errors", firstData.errors);
            const mutationErrors: Error[] = firstData.errors;
            mutationErrors.forEach((error) => {
                error.location = errorIdentifier.concat(error.location) as string[];
            });
            this.mutationErrors[this.state] = mutationErrors;
            return undefined;
        }

        return (firstData as unknown as SaveResult)?.id;
    }

    private nextStep(): void {
        this.setCurrentStep(this.currentStep + 1);
    }

    private setStateMessage(stepID: StepID, extra?: string, extraDesc?: MessageDescriptor): void {
        const step = steps[stepID];
        extra = extraDesc ? this.intl.formatMessage(extraDesc) : extra;
        this.setState(this.intl.formatMessage(step) + (extra ? " - " + extra : ""));
    }

    private toSnakeCase(word: string): string {
        return word
            .replace(/quota/, "")
            .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
            .replace(/^_/, "");
    }

    private getDeviceDefaults(data: DomainExportSection): AllocationInput[] {
        return Object.entries(JSON.parse(data.defaultDeviceLimits ?? "{}")).reduce((acc, [key, value]) => {
            const name = this.toSnakeCase(key);
            acc.push({
                name,
                value: value as number,
            });
            return acc;
        }, []);
    }

    private get errorLengthAll(): number {
        return Object.values(this.mutationErrors).reduce((acc, errors) => acc + errors.length, 0);
    }

    get errors(): CurrentStageErrors {
        for (const [stage, errors] of Object.entries(this.mutationErrors)) {
            if (errors.length > 0)
                return {
                    stage,
                    errors,
                };
        }

        return {
            stage: undefined,
            errors: [],
        };
    }

    private notify(domainId: number, name: string): void {
        this.dispatch(this.successNotification(domainId, name));
    }

    async runAllStages(): Promise<void> {
        if (!this.data) return;

        resetCacheAfterEveryMutationOptions.reset = false;

        this.benchmarker.tick();
        await this.getExistingDestinations();
        await this.getExistingVendors();
        await this.getExistingDeviceVariants();
        this.benchmarker.tick("preparation");

        const domainId = (await this.runStage("create_domain")) as number;
        this.setDomainId(domainId);

        await this.runStage("update_domain", domainId);
        await this.runStage("create_locations", domainId);
        await this.runStage("create_extensions", domainId);
        await this.runStage("create_groups", domainId);
        await this.runStage("create_ivrs", domainId);
        await this.runStage("create_faxes", domainId);
        await this.runStage("create_gateways", domainId);
        await this.runStage("create_gateway_routing", domainId);
        await this.runStage("add_domain_admins", domainId);
        await this.runStage("domain_address_book");

        resetCacheAfterEveryMutationOptions.reset = true;

        const benchmarkResults = this.benchmarker.summarize();

        if (this.cancelled) {
            console.debug("Import cancelled. Benchmark results:", benchmarkResults);
            return;
        }

        this.setState(undefined);
        if (!this.errorLengthAll) {
            console.debug("Import finished successfully. Benchmark results:", benchmarkResults);
            this.notify(domainId, this.data.domain.name);
            this.reset();
        } else {
            console.debug("Import finished with errors. Benchmark results:", benchmarkResults);
        }
    }

    async runStage(stage: StageID, id?: number): Promise<number | void> {
        if (this.cancelled) return;
        if (this.errorLengthAll) return;
        this.nextStep();
        if (this.mutations[stage]) {
            return await this.mutations[stage](id);
        }
    }

    private get dataCopy(): DomainExportYaml {
        return structuredClone(this.data);
    }

    async getExistingDestinations(): Promise<void> {
        const {data} = await this.destinationsQuery();
        data.destination?.items.forEach((item) => {
            this.destinationNames[item.name] = item.id;
        });
    }

    async getExistingVendors(): Promise<void> {
        const {data} = await this.vendorsQuery();
        data.vendors.forEach((item) => {
            this.vendorNames[item.name] = item.id;
        });
    }

    async getExistingDeviceVariants(): Promise<void> {
        const {data} = await this.deviceVariantsQuery();
        this.deviceVariants = new Set(
            data.deviceVariants.items.filter((item) => !item.domain).map((item) => item.name),
        );
    }

    async createDeviceVariant(name: string, domainId: number): Promise<boolean> {
        // Explicit generic parameters provided so TS doesn't try to unify a narrower variable type
        // (e.g. {name: string; domainId: number}) with the generated optional fields signature.
        const result = await this.resolveMutation(this.createDeviceVariantMutation({variables: {name, domainId}}), [
            "devices_provisioned",
        ]);
        if (!result) return false;

        this.deviceVariants.add(name);
        return true;
    }

    createDomain = async (): Promise<number> => {
        this.benchmarker.tick();

        const {domain, locations} = this.dataCopy;
        this.setStateMessage("creating_domain", domain.name);

        const id = (await this.resolveMutation(
            this.createDomainMutation({
                variables: {
                    name: domain.name,
                    description: domain.description,
                    domainType: domain.domainType,
                    dialoutPrefix: domain.dialoutPrefix,
                    specialNumbersDialoutPrefix: domain.specialNumbersDialoutPrefix,
                    shortNumbersLength: domain.shortNumbersLength,
                    preferDisplayNamesForStationNames: domain.preferDisplayNamesForStationNames,
                    locationName: locations[0].name,
                    locationDescription: locations[0].description,
                    locationPilotNumber: locations[0].pilotNumber,
                    smtpAuthEnabled: domain.smtpAuthEnabled,
                    smtpUsername: domain.smtpUsername,
                    smtpUserpass: domain.smtpUserpass,
                    privateRoutingType: handlePrivateRoutingType(domain.privateRoutingType),
                    defaultUserPackage: domain.defaultUserPackage ?? "",
                },
            }),
            [domain.name],
        )) as number;

        const {data: locationData} = await this.locationIdQuery({
            variables: {
                domainId: id,
                name: locations[0].name,
            },
        });

        if (locationData.locations.items[0].id) {
            this.locationIds[locations[0].name] = locationData.locations.items[0].id;
        }

        this.benchmarker.tick("create_domain");
        return id;
    };

    async updateDomainAllocationsAll(domainId: number): Promise<void> {
        const {domain} = this.dataCopy;

        const serviceEndpointAllocations = domain.allocations.serviceEndpoints;
        const capabilitySets = domain.allocations.capabilitySets;
        const deviceTypeSets = domain.allocations.deviceTypeSets;
        const userPackages = domain.allocations.userPackages;

        const servicesAllocations: ServicesAllocationInput = {};

        for (const allocation of serviceEndpointAllocations) {
            const allocationName = snakeToCamelCase(
                SERVICE_ALLOCATION_KEY_MAP[allocation.name as keyof typeof SERVICE_ALLOCATION_KEY_MAP] ??
                    allocation.name,
            );
            (servicesAllocations[allocationName as keyof ServicesAllocationInput] as any) = allocation.value;
        }

        await this.updateDomainAllocations({
            domainId,
            userPackages,
            deviceTypeSets,
            capabilitySets,
            services: servicesAllocations,
        });
    }

    async updateDomainAllocations(variables: DomainAllocationUpdateMutationVariables): Promise<void> {
        this.setStateMessage("updating_domain_allocations");
        await this.resolveMutation(this.updateDomainAllocationMutation({variables}), ["domain_allocations"]);
    }

    async updateDomainDefaults(domainId: number): Promise<void> {
        const {domain} = this.dataCopy;

        await this.resolveMutation(
            this.updateDomainDefaultMutation({
                variables: {
                    domainId,
                    devices: this.getDeviceDefaults(domain),
                    capabilities: domain.defaultCapabilitySetForExtensions ?? [],
                },
            }),
            ["domain_defaults"],
        );
    }

    async updateDomainSettings(id: number): Promise<void> {
        this.setStateMessage("updating_domain_settings");
        const {domain} = this.dataCopy;

        await this.resolveMutation(
            this.updateDomainSettingsMutation({
                variables: {
                    input: {
                        id,
                        description: domain.description,
                        companyName: domain.companyName,
                        preferDisplayNamesForStationNames: domain.preferDisplayNamesForStationNames,
                        specialNumbersDialoutPrefix: domain.specialNumbersDialoutPrefix,
                        smtp: {
                            enabled: domain.smtpAuthEnabled,
                            username: domain.smtpUsername,
                            userpass: domain.smtpUserpass,
                        },
                        pinBasedCalls: domain.pinBasedCalls,
                        privateRoutingType: handlePrivateRoutingType(domain.privateRoutingType),
                        dialoutPrefix: domain.dialoutPrefix,
                        attendantPrefix: domain.attendantPrefix,
                        requirePinForStartingPrivateCalls: domain.requirePinForStartingPrivateCalls,
                        groupDomain: domain.groupDomain,
                        faxSignature: domain.faxSignature,
                        baseColor: domain.baseColor,
                        login: {
                            backgroundColor: domain.loginBackgroundColor,
                        },
                        background: {
                            isCentered: domain.backgroundIsCenter,
                        },
                        outgoingCallBarring: {
                            admin: domain.outgoingCallBarringAdmin,
                            operator: domain.outgoingCallBarringOperator,
                        },
                        recording: {
                            location: nullIfEmpty(domain.recordingLocation),
                            retention: domain.recordingRetention,
                        },
                    },
                },
            }),
            ["domain_settings"],
        );
    }

    async updateDomainE164Ranges(domainId: number): Promise<void> {
        const ranges: string[][] = JSON.parse(this.dataCopy.domain.endpointE164RangesForAdmins ?? "[]");

        await this.resolveMutation(
            this.updateDomainE164RangesMutation({
                variables: {
                    id: domainId,
                    ranges: ranges.map(([start, end]) => ({start, end})),
                },
            }),
            ["domain_e164_ranges"],
        );
    }

    updateDomain = async (id: number): Promise<void> => {
        this.benchmarker.tick();
        await this.updateDomainSettings(id);
        await this.updateDomainDefaults(id);
        await this.updateDomainAllocationsAll(id);
        await this.updateDomainE164Ranges(id);
        this.benchmarker.tick("update_domain");
    };

    createLocations = async (domainId: number): Promise<void> => {
        const copy = this.dataCopy;
        for (const location of copy.locations.slice(1)) {
            if (!this.cancelled) await this.createLocation(location, domainId);
        }
    };

    async createLocation(location: LocationExport, domainId: number) {
        this.benchmarker.tick();

        this.setStateMessage("creating_locations", location.name);
        const id = (await this.resolveMutation(
            this.createLocationMutation({
                variables: {
                    domainId,
                    name: location.name,
                    callLimit: location.callLimit,
                    bandwidthLimit: location.bandwidthLimit,
                    description: location.description,
                    ipAddress: location.ipAddress,
                    pilotNumber: location.pilotNumber,
                    emergencyPrefix: location.emergencyPrefix,
                },
            }),
            [location.name],
        )) as number;

        this.locationIds[location.name] = id;
        this.benchmarker.tick("create_locations");
    }

    createExtensions = async (domainId: number): Promise<void> => {
        const copy = this.dataCopy;
        const extensionsWithIds: [ExtensionExport, number][] = [];
        for (const extension of copy.users) {
            if (this.cancelled) return;
            const id = await this.createExtension(extension);
            if (!id) continue;
            extensionsWithIds.push([extension, id]);
        }

        const {data: dtsResult} = await this.deviceTypeSetsQuery();

        // these data structures are used to cache, and only live for the lifetime
        // of the following 2 loops
        const extensionNumberIdToDTSAllocations = {};
        const provisionedMacToId = {};

        for (const [extension, extensionId] of extensionsWithIds) {
            if (this.cancelled) return;
            await this.createDevicesForExtension(extension, extensionId, domainId, provisionedMacToId);
        }

        for (const [extension] of extensionsWithIds) {
            if (this.cancelled) return;
            this.benchmarker.tick();
            await this.createLinkedDevicesForExtension(
                extension,
                provisionedMacToId,
                getDTSWithLinkedDevice(dtsResult.deviceTypeSets),
                extensionNumberIdToDTSAllocations,
            );
            this.benchmarker.tick("create_devices_linked");
        }
    };

    async createExtension(extension: ExtensionExport): Promise<number | undefined> {
        this.benchmarker.tick();
        this.setStateMessage("creating_extensions", extension.displayName);

        const speedDials: CreateUserSpeedDial[] = [];
        const addressBookEntries: AddressBookEntryData[] = [];

        for (const sd of extension.speeddials) {
            speedDials.push({
                shortNumber: sd.shortNumber,
                targetNumber: sd.targetNumber,
                description: sd.description,
            });

            if (sd.addressBookGivenName || sd.addressBookFamilyName) {
                addressBookEntries.push({
                    givenName: sd.addressBookGivenName,
                    sn: sd.addressBookFamilyName,
                    telephoneNumber: [sd.targetNumber],
                    description: sd.description,
                });
            }
        }

        const id = (await this.resolveMutation(
            this.createExtensionMutation({
                variables: {
                    data: {
                        locationId: {...this.locationIds}[extension.location.name],
                        capabilitySets: extension.limits.capabilitySets,
                        deviceTypeSets: extension.limits.deviceTypeSets,
                        displayName: extension.displayName,
                        e164: nullIfEmpty(extension.number.e164),
                        pin: extension.pin,
                        shortNumber: extension.number.shortNumber,
                        userPackageName: extension.userPackageName,
                        profiles: extension.profiles.map((profile) => ({
                            name: profile.name,
                            active: profile.active,
                            anonymousCallRejection: profile.anonymousCallRejection,
                            callForwardBusy: {
                                enabled: profile.callForwardBusy,
                                number: nullIfEmpty(profile.callForwardBusyNumber),
                            },
                            callForwardFault: {
                                enabled: profile.callForwardFault,
                                number: nullIfEmpty(profile.callForwardFaultNumber),
                            },
                            callForwardNoanswer: {
                                enabled: profile.callForwardNoanswer,
                                number: nullIfEmpty(profile.callForwardNoanswerNumber),
                                timeout: profile.callForwardNoanswerTimeout,
                            },
                            callForwardUnavailable: {
                                enabled: profile.callForwardUnavailable,
                                number: nullIfEmpty(profile.callForwardUnavailableNumber),
                            },
                            parallelRinging: {
                                enabled: profile.parallelRinging,
                                number: nullIfEmpty(profile.parallelRingingNumber),
                            },
                            callingNamePresentation: profile.callingNamePresentation,
                            callingNumberPresentationRestriction: profile.callingNumberPresentationRestriction,
                            connectedNumberPresentationRestriction: profile.connectedNumberPresentationRestriction,
                            doNotDisturb: profile.doNotDisturb,
                            locked: profile.locked,
                            rejectDivertedCalls: profile.rejectDivertedCalls,
                            secretaryMode: profile.secretaryMode,
                            stickyProfile: profile.stickyProfile,
                            settings: {
                                // Even though this looks odd, we need to do this, because the
                                // export format flattens the activation times,
                                // but the mutation expects them grouped by weekday + times array.
                                // It is fine to just group them by themselves, it will work
                                // as expected.
                                activationTimes: (profile.activationTimes ?? []).map((at) => ({
                                    times: [at.activationTime],
                                    weekdays: [at.weekday],
                                })),
                                specialDays: profile.specialDays,
                            },
                            rules: profile.rules.map((rule) => ({
                                ...rule,
                                targetNumber: nullIfEmpty(rule.targetNumber),
                            })),
                        })),
                        login: extension.login,

                        callLimit: extension.callLimit,
                        callWaiting: extension.callWaiting,
                        cliro: extension.cliro,
                        connectedNumberPresentationRestrictionOverride:
                            extension.connectedNumberPresentationRestrictionOverride,
                        directInwardDialing: extension.directInwardDialing,
                        directOutwardDialing: extension.directOutwardDialing,
                        homeNumber: nullIfEmpty(extension.homeNumber),
                        internationalCac: extension.internationalCac,
                        mobileNumber: nullIfEmpty(extension.mobileNumber),
                        nationalCac: extension.nationalCac,
                        notificationEmail: extension.notificationEmail,
                        nonPaymentBlockingType: extension.number.nonPaymentBlockingType,
                        outgoingCallBarringAdmin: extension.outgoingCallBarringAdmin,
                        outgoingCallBarringOperator: extension.outgoingCallBarringOperator,
                        outgoingCallBarringUser: extension.outgoingCallBarringUser,
                        previewFeaturesEnabled: extension.previewFeaturesEnabled,
                        privateRoutingType: handlePrivateRoutingType(extension.privateRoutingType),
                        voicemailToEmailEnabled: extension.voicemailToEmailEnabled,
                        secretaryNumberId: extension.secretaryNumberId
                            ? this.numberIds[extension.secretaryNumberId]
                            : undefined,

                        hotNumber: this.structureConfig.isEnabledFeature("hotNumber") ? extension.hotnumber : undefined,
                        maliciousCallTrace: this.structureConfig.isEnabledFeature("maliciousCallTrace")
                            ? extension.maliciousCallTrace
                            : undefined,

                        speedDials: speedDials,
                    },
                },
            }),
            ["extension_create", extension.number.shortNumber],
        )) as number;

        if (!id) return undefined;
        this.benchmarker.tick("create_extensions");

        const {data: info} = await this.extensionNumberIdQuery({
            variables: {
                userId: id,
            },
            fetchPolicy: "no-cache",
        });

        const newNumberId = info.users.items[0].numberId;
        this.numberIds[extension.number.shortNumber] = newNumberId;
        this.extensionIds[extension.id] = id;
        this.extensionNumberIdToId[newNumberId] = id;

        await this.addExtensionSpeedDialAddressBookEntries(extension.number.shortNumber, addressBookEntries);
        this.benchmarker.tick("create_extension_address_book_from_speed_dials");

        // TODO: if there are linked devices for this extension, then update the allocations
        // accordingly
        // (we can get the information from the extension that POINTS to this extension)

        return id;
    }

    async addExtensionSpeedDialAddressBookEntries(extensionShortNumber: string, entries: AddressBookEntryData[]) {
        const domainName = this.data.domain.name;
        const target = `${extensionShortNumber}@${domainName}`;

        for (const entry of entries) {
            await this.resolveMutation(
                this.addAddressBookEntry({
                    variables: {
                        target,
                        entry,
                    },
                }),
                ["extension_create", extensionShortNumber],
            );
        }
    }

    async createLinkedDevicesForExtension(
        extension: ExtensionExport,
        provisionedMacToId: Record<string, number>,
        dtsWithLinkedDevices: string[],
        extensionNumberIdToDTSAllocations: Record<number, Record<string, AllocationInput>>,
    ) {
        // NOTE: due to some reasons, the exported data from an old system might not have
        // enough linked device quota for users, therefore when importing provisioned
        // device buttons, if we get a limit error for linked devices, we try to
        // increase them until we succeed or run out of linked device system allocations

        let linkedDts = dtsWithLinkedDevices.pop();
        const deviceLimits = extension.limits.deviceTypeSets;
        for (const provisionedDevice of extension.devices.provisioned) {
            const deviceId = provisionedMacToId[provisionedDevice.macAddress];

            type SaveButtonsResult = {
                type: "ok" | "error" | "linked_limit";
                errors?: Error[];
                limitError?: Error;
            };

            /** helper function to save the phone buttons for the provisioned device
             * we will call this function multiple times with different linked device allocations */
            const saveButtons = async (): Promise<SaveButtonsResult> => {
                const result = await this.savePhoneButtonsMutation({
                    variables: {
                        provisionedDeviceId: deviceId,
                        buttons: (provisionedDevice.buttons as PhoneButton[]).map((button) => ({
                            buttonPosition: button.buttonPosition,
                            buttonType: button.buttonType,
                            display: button.display,
                            speedDialNumber: button.speedDialNumber,
                            targetNumberId: this.getCurrentNumberId(button.targetNumberId),
                        })),
                    },
                });

                // if we do not have any errors, we are done
                const buttonsErrors = result.data?.savePhoneButtons?.errors;
                if (!buttonsErrors?.length) {
                    return {type: "ok"};
                }

                // we do the same thing this.mutate does with the errors
                for (const err of buttonsErrors) {
                    err.location = ["extension_devices_buttons", extension.number.shortNumber].concat(err.location);
                }

                // if it is a limit error, there will be only one error returned
                const buttonError = buttonsErrors[0];
                if (buttonError.type === "value_error.quota_exceeded") {
                    return {
                        type: "linked_limit",
                        limitError: buttonError,
                    };
                } else {
                    // if it is not linked device limit error, we just return the error
                    // and end the loop
                    return {type: "error", errors: buttonsErrors};
                }
            };

            /** helper function to update the extension limit for linked devices
             * it will combine the existing allocations with the modified one
             * and save the new limit */
            const updateLimit = async (
                modifiedAllocation: AllocationInput,
                targetNumberId: number,
            ): Promise<Error[] | undefined> => {
                const allSavedAllocations = Object.values(extensionNumberIdToDTSAllocations[targetNumberId] ?? {});

                // combine extension.limits.deviceTypeSets, allSavedAllocations and the modified one by their .name property
                const newAllocations = structuredClone(extension.limits.deviceTypeSets);

                for (const savedAllocation of allSavedAllocations) {
                    const existingAllocation = newAllocations.find((a) => a.name === savedAllocation.name);
                    if (existingAllocation) {
                        existingAllocation.value = savedAllocation.value;
                    } else {
                        newAllocations.push(savedAllocation);
                    }
                }

                const existingAllocation = newAllocations.find((a) => a.name === modifiedAllocation.name);
                if (existingAllocation) {
                    existingAllocation.value = modifiedAllocation.value;
                } else {
                    newAllocations.push(modifiedAllocation);
                }

                const limitResult = await this.updateExtensionLimitMutation({
                    variables: {
                        id: this.extensionNumberIdToId[targetNumberId],
                        deviceTypeSets: newAllocations,
                    },
                });

                const limitErrors = limitResult.data?.updateUserLimit?.errors;
                if (limitErrors?.length) {
                    for (const err of limitErrors) {
                        err.location = ["extension_devices_buttons", extension.number.shortNumber].concat(err.location);
                    }
                    return limitErrors;
                }

                return undefined;
            };

            /** this will indicate which dts did we increase in the previous step,
             * so that we know if the next iteration will succeed, we need to save this
             * allocation to reuse it later on if we need even more allocations */
            let linkedAllocation: AllocationInput = undefined;

            /** this is the user's number id which the quota was increased in the previous iteration */
            let targetNumberId: number = undefined;

            const saveLinkedAllocationForTargetNumberId = () => {
                extensionNumberIdToDTSAllocations[targetNumberId] ??= {};
                extensionNumberIdToDTSAllocations[targetNumberId][linkedAllocation.name] = linkedAllocation;
            };

            // we start an infinite loop first, as we do not yet know how many buttons
            // need increased quota
            linkedDeviceLoop: while (true) {
                if (linkedAllocation) {
                    // as we indicated at the end of the loop, we will update the target number user's
                    // quota, so that the save buttons might succeed with the increased quota
                    const updateLimitErrors = await updateLimit(linkedAllocation, targetNumberId);

                    // if we have any errors and no dts are left to increase, we must exit
                    if (updateLimitErrors) {
                        if (linkedDts === undefined) {
                            this.mutationErrors[this.state] = updateLimitErrors;
                            return;
                        } else {
                            linkedDts = dtsWithLinkedDevices.pop();
                            continue linkedDeviceLoop;
                        }
                    }
                }

                const buttonsResult = await saveButtons();
                if (buttonsResult.type === "ok") {
                    if (linkedAllocation) {
                        // if we successfully saved the buttons with the increased quota,
                        // we need to save the allocation, to not forget this change also needs to be included
                        // the next time we need more allocations again
                        saveLinkedAllocationForTargetNumberId();
                    }

                    // if everything went well, we have nothing more to do
                    break linkedDeviceLoop;
                } else if (buttonsResult.type === "error") {
                    // if we got an error that is not linked device limit error, we just return
                    // (the mutation errors have already been set)
                    this.mutationErrors[this.state] = buttonsResult.errors;
                    return;
                } else if (linkedDts === undefined) {
                    // if there are no more linked device dts to increase,
                    // set the mutation errors and exit the loop
                    this.mutationErrors[this.state] = [buttonsResult.limitError];
                    return;
                }

                const newTargetNumberId = parseContext(buttonsResult.limitError.context)?.numberId;
                if (newTargetNumberId === undefined) {
                    // probably this will never happen, but just in case
                    console.error("Cannot parse target number id from error context", buttonsResult.limitError.context);
                    this.mutationErrors[this.state] = [buttonsResult.limitError];
                    break linkedDeviceLoop;
                }

                // it means that the previous quota increase worked, but now another linked user needs
                // quota increase
                if (targetNumberId !== undefined && newTargetNumberId !== targetNumberId) {
                    saveLinkedAllocationForTargetNumberId();
                }

                targetNumberId = newTargetNumberId;

                // I had some errors when I did not clone the allocation before modifying it,
                // so just in case.
                // If we already have a saved allocation (meaning we have increased the quota sometime before),
                // let's use that, otherwise we will use the limits from the import data.
                linkedAllocation = structuredClone(
                    extensionNumberIdToDTSAllocations[targetNumberId]?.[linkedDts] ??
                        deviceLimits.find((d) => d.name === linkedDts) ?? {
                            name: linkedDts,
                            value: 0,
                        },
                );

                // let's increase the allocation by 1, which will be enough for the next iteration
                // to succeed (in case we have enough system limit for this dts)
                linkedAllocation.value += 1;
            }
        }
    }

    async createDevicesForExtension(
        extension: ExtensionExport,
        extensionId: number,
        domainId: number,
        provisionedMacToId: Record<string, number>,
    ): Promise<void> {
        this.setStateMessage("creating_extensions_devices", extension.displayName);

        for (const clientDevice of extension.devices.client) {
            if (this.cancelled) return;
            this.benchmarker.tick();
            await this.resolveMutation(
                this.saveClientDeviceMutation({
                    variables: {
                        userId: extensionId,
                        clientType: getEnumByValue(ClientType, clientDevice.type),
                        description: clientDevice.description,
                    },
                }),
                ["extension_devices_client", extension.number.shortNumber],
            );
            this.benchmarker.tick("create_devices_client");
        }

        for (const thirdPartyDevice of extension.devices.thirdParty) {
            if (this.cancelled) return;
            this.benchmarker.tick();
            await this.resolveMutation(
                this.saveThirdPartyDeviceMutation({
                    variables: {
                        userId: extensionId,
                        description: thirdPartyDevice.description,
                        sipUser: thirdPartyDevice.sipUser,
                        sipPassword: thirdPartyDevice.sipPassword,
                    },
                }),
                ["extension_devices_third_party", extension.number.shortNumber],
            );
            this.benchmarker.tick("create_devices_third_party");
        }

        for (const provisionedDevice of extension.devices.provisioned) {
            if (this.cancelled) return;
            if (provisionedDevice.variant !== "" && !this.deviceVariants.has(provisionedDevice.variant)) {
                this.benchmarker.tick();
                const res = await this.createDeviceVariant(provisionedDevice.variant, domainId);
                this.benchmarker.tick("create_device_variants");
                if (!res) return;
            }

            this.benchmarker.tick();
            const deviceId = (await this.resolveMutation(
                this.saveProvisionedDeviceMutation({
                    variables: {
                        userId: extensionId,
                        macAddress: provisionedDevice.macAddress,
                        // HACK: 0 is to get backend error instead of GraphQL error
                        vendorId: this.vendorNames[provisionedDevice.vendor.name] ?? 0,
                        variant: provisionedDevice.variant,
                        provisioning: provisionedDevice.provisioning,
                        useDisplayNameForStationName: provisionedDevice.useDisplayNameForStationName,
                    },
                }),
                ["extension_devices_provisioned", extension.number.shortNumber],
            )) as number;

            if (!deviceId) continue;
            provisionedMacToId[provisionedDevice.macAddress] = deviceId;
            this.benchmarker.tick("create_devices_provisioned");
        }
    }

    createIvrs = async (domainId: number): Promise<void> => {
        const copy = this.dataCopy;
        const ivrsWithIds: [IvrExport, number][] = [];

        for (const ivr of copy.ivrs.filter((ivr) => ivr.number.shortNumber)) {
            if (this.cancelled) return;
            const id = await this.createIvr(ivr, domainId);
            if (!id) continue;
            ivrsWithIds.push([ivr, id]);
        }

        for (const [ivr, id] of ivrsWithIds) {
            if (this.cancelled) return;
            this.setStateMessage("updating_ivr_profiles", ivr.name);
            await this.updateIvrsProfiles(ivr, id);
        }
    };

    async updateIvrsProfiles(ivr: IvrExport, id: number): Promise<void> {
        const {data} = await this.ivrDataQuery({
            variables: {
                ivrId: id,
            },
            fetchPolicy: "no-cache",
        });

        const fetchedProfiles = data.ivrs.items[0].profiles;
        fetchedProfiles.sort((a, b) => a.id - b.id);

        for (const [profile, exportProfile] of zip(fetchedProfiles, ivr.profiles)) {
            if (this.cancelled) return;
            this.benchmarker.tick();

            const data: UpdateIvrProfileMutationVariables = {
                id: profile.id,
                name: exportProfile.name,
                instantConnect: exportProfile.instantConnect,
                timeout: exportProfile.timeout,
                greetings: await Promise.all(
                    exportProfile.greetings.map((greeting) => this.getUpdateIvrMenuMedia(greeting)),
                ),
                menuMedia: await Promise.all(exportProfile.menuMedia.map((media) => this.getUpdateIvrMenuMedia(media))),
                settings: {
                    activationTimes: exportProfile.activationTimes.map((actTime) => ({
                        times: [actTime.activationTime],
                        weekdays: [actTime.weekday],
                    })),
                    specialDays: exportProfile.specialDays.map((specDay) => ({
                        day: specDay.day,
                        activationTime: specDay.activationTime,
                        everyYear: specDay.everyYear,
                    })),
                    stickyProfile: exportProfile.stickyProfile,
                },
                buttons: await this.getIvrButtons(exportProfile.buttons),
                submenus: await Promise.all(
                    exportProfile.submenus.map(async (submenu) => ({
                        instantConnect: submenu.instantConnect,
                        timeout: submenu.timeout,
                        path: submenu.path,
                        menuMedia: await Promise.all(
                            submenu.menuMedia.map((media) => this.getUpdateIvrMenuMedia(media)),
                        ),
                        buttons: await this.getIvrButtons(submenu.buttons),
                    })),
                ),
            };

            await this.resolveMutation(this.updateIvrProfileMutation({variables: data}), [
                ivr.name ?? ivr.number.shortNumber,
            ]);
            this.benchmarker.tick("update_ivr_profiles");

            if (exportProfile.active) {
                this.benchmarker.tick();
                await this.resolveMutation(
                    this.activateIvrProfileMutation({
                        variables: {
                            id: profile.id,
                        },
                    }),
                    ["ivr_profile_activate", ivr.name ?? ivr.number.shortNumber],
                );
                this.benchmarker.tick("activate_ivr_profiles");
            }
        }
    }

    private async getUpdateIvrMenuMedia(exportMedia: IvrMediaExport): Promise<UpdateIvrMenuMedia> {
        return {
            description: exportMedia.description,
            disabled: exportMedia.disabled,
            file: await this.getMediaFile(exportMedia.mediaId),
        };
    }

    private async getIvrButtons(buttons: IvrExport["profiles"][number]["buttons"]): Promise<UpdateIvrButton[]> {
        return await Promise.all(
            buttons.map(async (button) => {
                if (button.actionType === IvrActionType.Submenu) delete button.targetNumberId;
                else if (button.targetNumberId !== undefined)
                    button.targetNumberId = this.getCurrentNumberId(button.targetNumberId);

                let connectionPrompt: UpdateConnectionPrompt = undefined;
                if (button.connectionPrompt?.mediaId !== undefined && !button.connectionPrompt.disabled) {
                    connectionPrompt = {
                        file: await this.getMediaFile(button.connectionPrompt.mediaId),
                        description: button.connectionPrompt.description,
                    };
                }

                const update: UpdateIvrButton = {
                    actionType: button.actionType,
                    targetNumberId: button.targetNumberId,
                    targetE164: button.targetE164,
                    connectionPrompt,
                };

                return update;
            }),
        );
    }

    async createIvr(ivr: IvrExport, domainId: number): Promise<number | undefined> {
        this.setStateMessage("creating_ivrs", ivr.name ?? ivr.number.shortNumber);

        this.benchmarker.tick();
        const id = (await this.resolveMutation(
            this.createIvrMutation({
                variables: {
                    domainId,
                    name: ivr.name,
                    identifier: ivr.number.shortNumber,
                    e164: ivr.number.e164,
                },
            }),
            ["ivr_create", ivr.name ?? ivr.number.shortNumber],
        )) as number;
        this.benchmarker.tick("create_ivrs");

        if (!id) return undefined;

        this.benchmarker.tick();
        await this.resolveMutation(
            this.updateIvrMutation({
                variables: {
                    id,
                    name: ivr.name,
                    recordingType: ivr.recordingType,
                    nonPaymentBlockingType: ivr.number.nonPaymentBlockingType,
                    outgoingCallBarringAdmin: ivr.outgoingCallBarringAdmin,
                    outgoingCallBarringOperator: ivr.outgoingCallBarringOperator,
                    callLimit: ivr.callLimit,
                },
            }),
            ["ivr_update", ivr.name ?? ivr.number.shortNumber],
        );
        this.benchmarker.tick("update_ivrs");

        const {data: info} = await this.ivrNumberIdQuery({
            variables: {
                ivrId: id,
            },
            fetchPolicy: "no-cache",
        });

        this.numberIds[ivr.number.shortNumber] = info.ivrs.items[0].number.id;

        return id;
    }

    createFaxes = async (domainId: number): Promise<void> => {
        const {faxes} = this.dataCopy;
        for (const fax of faxes) {
            if (this.cancelled) return;
            await this.createFax(fax, domainId);
        }
    };

    async createFax(fax: FaxExport, domainId: number): Promise<void> {
        this.benchmarker.tick();
        this.setStateMessage("creating_faxes", fax.number.shortNumber);
        const id = (await this.resolveMutation(
            this.createFaxMutation({
                variables: {
                    domainId,
                    e164: fax.number.e164,
                    shortNumber: fax.number.shortNumber,
                    outgoingCallBarringOperator: fax.outgoingCallBarringOperator,
                    outgoingCallBarringAdmin: fax.outgoingCallBarringOperator,
                    incomingEmailAddress: fax.incomingEmailAddress,
                    senderEmailAddress: fax.senderEmailAddress,
                    password: fax.password,
                    notifyUnsuccessfulReceiving: fax.notifyUnsuccessfulReceiving,
                    additionalFaxSendingEmails: [],
                },
            }),
            ["fax_create", fax.number.shortNumber],
        )) as number;

        this.benchmarker.tick("create_faxes");
        if (!id) return;

        for (const email of fax.additionalEmails) {
            email.extensionId = this.extensionIds[email.extensionId];
        }

        this.benchmarker.tick();
        await this.resolveMutation(
            this.updateFaxMutation({
                variables: {
                    id,
                    outgoingCallBarringAdmin: fax.outgoingCallBarringAdmin,
                    outgoingCallBarringOperator: fax.outgoingCallBarringOperator,
                    nonPaymentBlockingType: fax.number.nonPaymentBlockingType,
                    headerInfo: fax.headerInfo,
                    headerNumber: fax.headerNumber,
                    incomingEmailAddress: fax.incomingEmailAddress,
                    senderEmailAddress: fax.senderEmailAddress,
                    password: fax.password,
                    notifyUnsuccessfulReceiving: fax.notifyUnsuccessfulReceiving,
                    additionalFaxSendingEmails: {
                        emails: fax.additionalEmails.map((email) => ({
                            id: 0,
                            userId: email.extensionId,
                            displayName: email.extension.displayName,
                            shortNumber: email.extension.number.shortNumber,
                            notificationEmail: email.extension.notificationEmail,
                            enableBodyProcess: email.enableBodyProcess,
                            enableProgressFeedback: email.enableProgressFeedback,
                        })),
                    },
                },
            }),
            ["fax_update", fax.number.shortNumber],
        );
        this.benchmarker.tick("update_faxes");
    }

    createGateways = async (domainId: number): Promise<void> => {
        const {gateways} = this.dataCopy;
        for (const gateway of gateways) {
            if (this.cancelled) return;
            await this.createGateway(gateway, domainId);
        }
    };

    createGatewayRoutings = async (domainId: number) => {
        this.benchmarker.tick();
        const {gateways} = this.dataCopy;
        const routings = gateways.flatMap((gateway) => gateway.routings ?? []);
        await this.resolveMutation(
            this.updateGatewayRoutingMutation({
                variables: {
                    domainId,
                    gatewayRoutings: routings.map((routing) => ({
                        comment: routing.comment,
                        destinationId: this.destinationNames[routing.destination.name] ?? -1,
                        gatewayId: this.gatewayIds[routing.gatewayId],
                        priority: routing.priority,
                    })),
                },
            }),
            ["gateway_routings"],
        );
        this.benchmarker.tick("update_gateway_routings");
    };

    async createGateway(gateway: GatewayExport, domainId: number) {
        this.benchmarker.tick();
        const id = (await this.resolveMutation(
            this.createGatewayMutation({
                variables: {
                    domainId,
                    e164: gateway.number.e164,
                    shortNumber: gateway.number.shortNumber,
                    displayName: gateway.displayName,
                    sipUser: gateway.sipUser,
                    sipPassword: gateway.sipPassword,
                    enabled: gateway.enabled,
                    callbackNumberId: this.getCurrentNumberId(gateway.callbackNumberId),
                },
            }),
            ["gateway_create", gateway.number.shortNumber],
        )) as number;

        this.benchmarker.tick("create_gateways");
        if (!id) return;

        this.gatewayIds[gateway.id] = id;

        this.benchmarker.tick();
        await this.resolveMutation(
            this.updateGatewayMutation({
                variables: {
                    id,
                    data: {
                        callbackNumberId: this.getCurrentNumberId(gateway.callbackNumberId),
                        callbackType: gateway.callbackType,
                        displayName: gateway.displayName,
                        enabled: gateway.enabled,
                        multipleCallsNumber:
                            gateway.multipleCallsNumber === 0 ? undefined : gateway.multipleCallsNumber,
                        nonPaymentBlockingType: gateway.number.nonPaymentBlockingType,
                        outgoingCallBarringOperator: gateway.outgoingCallBarringOperator,
                        sipUser: gateway.sipUser,
                        sipPassword: gateway.sipPassword,
                    },
                },
            }),
            ["gateway_update", gateway.number.shortNumber],
        );
        this.benchmarker.tick("update_gateways");
    }

    createGroups = async (domainId: number): Promise<void> => {
        const {groups} = this.dataCopy;
        for (const group of groups) {
            if (this.cancelled) return;
            this.benchmarker.tick();
            this.setStateMessage("creating_groups", group.name);

            // only pickup groups have no number
            if (group.number?.e164 == null) {
                await this.savePickupGroup(group, domainId);
                this.benchmarker.tick("save_pickup_groups");
                continue;
            }

            const id = await this.createCallGroup(group, domainId);
            this.benchmarker.tick("create_groups");
            if (!id) continue;

            if (group.groupType === GroupType.CallQueue) {
                await this.updateCallQueue(group, id);
                this.benchmarker.tick("update_call_queues");
            } else {
                await this.updateCallGroup(group, id);
                this.benchmarker.tick("update_call_groups");
            }
        }
    };

    async createCallGroup(group: GroupExport, domainId: number): Promise<number | undefined> {
        const groupType = group.groupType as unknown as CallGroupType;

        // as the create call group now accepts members,
        // it has the following api: list[list[CreateCallGroupMember]] (section[list-of-members])
        // we need to first group the members by sections, then create the ordered list
        const sectionatedMembers = group.members
            .sort((a, b) => a.sectionIndex - b.sectionIndex || a.callOrder - b.callOrder)
            .reduce<Record<number, CreateCallGroupMember[]>>((acc, member) => {
                const section = acc[member.sectionIndex] ?? [];
                section.push({
                    numberId: this.numberIds[member.number.shortNumber],
                    controllingLine: member.controllingLine,
                });
                acc[member.sectionIndex] = section;
                return acc;
            }, {});
        const members = Object.values(sectionatedMembers);

        const id = (await this.resolveMutation(
            this.createCallGroupMutation({
                variables: {
                    domainId,
                    type: groupType,
                    name: group.name,
                    shortNumber: group.number.shortNumber,
                    e164: nullIfEmpty(group.number.e164),
                    pickup: group.pickup,
                    sequentialTimeout: group.sequentialTimeout,
                    // explicitly set to undefined, otherwise it will be set to null causing backend to validate
                    // and essentially returning an error due to invalid value (can not be null)
                    // but setting it to undefined will not send the value at all
                    size: group.size ?? undefined,
                    members,
                },
            }),
            ["call_group_create", group.name],
        )) as number;

        if (!id) return undefined;

        const {data: numberIdQueryData} = await this.callGroupNumberIdQuery({
            variables: {
                id,
            },
            fetchPolicy: "no-cache",
        });

        this.groupIds[group.name] = id;
        this.numberIds[group.number.shortNumber] = numberIdQueryData.groups.items[0].numberId;
        return id;
    }

    async updateCallGroup(group: GroupExport, id: number) {
        const groupType = group.groupType as unknown as CallGroupType;
        await this.resolveMutation(
            this.updateCallGroupMutation({
                variables: {
                    id,
                    name: group.name,
                    type: groupType,
                    pickup: group.pickup,
                    sequentialTimeout: group.sequentialTimeout,
                    outgoingCallBarringOperator: group.outgoingCallBarringOperator,
                    outgoingCallBarringAdmin: group.outgoingCallBarringAdmin,
                },
            }),
            ["call_group_update", group.name],
        );
    }

    async updateCallQueue(group: GroupExport, id: number) {
        await this.resolveMutation(
            this.updateCallQueueMutation({
                variables: {
                    id,
                    name: group.name,
                    sequentialTimeout: group.sequentialTimeout,
                    huntingAlgorithmType: group.huntingAlgorithmType,
                    maxQueueLength: group.maxQueueLength,
                    maxWaitingTime: group.maxWaitingTime,
                    acwLength: group.acwLength,
                    outgoingCallBarringAdmin: group.outgoingCallBarringAdmin,
                    outgoingCallBarringOperator: group.outgoingCallBarringOperator,
                    hasAddressBook: group.hasAddressBook,
                },
            }),
            ["call_group_update", group.name],
        );
    }

    async savePickupGroup(pickupGroup: GroupExport, domainId: number) {
        const id = (await this.resolveMutation(
            this.savePickupGroupMutation({
                variables: {
                    domainId,
                    name: pickupGroup.name,
                },
            }),
            ["call_group_create", pickupGroup.name],
        )) as number;

        if (!id) return;

        this.groupIds[pickupGroup.name] = id;
    }

    importDomainAdmins = async (domainId: number): Promise<void> => {
        this.benchmarker.tick();
        this.setStateMessage("importing_domain_admins");
        const {domainAdmins: domainAdminsYaml} = this.dataCopy;
        if (!domainAdminsYaml) return;

        const domainAdmins = domainAdminsYaml.map((admin) => ({
            aliases: admin.aliases,
            ...admin.user,
        }));

        await this.resolveMutation(this.addDomainAdminsMutation({variables: {domainId, domainAdmins}}), [
            "domain_admins",
        ]);
        this.benchmarker.tick("import_domain_admins");
    };

    importDomainAddressBook = async (): Promise<void> => {
        this.benchmarker.tick();
        this.setStateMessage("importing_domain_address_book");
        const {domain, addressBook} = this.dataCopy;
        if (!addressBook) return;

        for (const entry of addressBook) {
            await this.resolveMutation(
                this.addAddressBookEntry({
                    variables: {
                        target: domain.name,
                        entry: {
                            ...entry,
                        },
                    },
                }),
                ["domain_address_book"],
            );
            this.benchmarker.tick("add_address_book_entries");
        }
    };

    async loadFromFile(fileContent: string): Promise<void> {
        const data: DomainExportYaml = parse(fileContent);
        this.setData(data);
    }

    canSubmit(): boolean {
        return !!this.data;
    }

    /**
     * The stages with their corresponding function.
     * This is only for making the class methods cleaner
     * to follow.
     **/
    private mutations: Record<StageID, CreateMutation | UpdateMutation> = {
        create_domain: this.createDomain,
        update_domain: this.updateDomain,
        create_locations: this.createLocations,
        create_extensions: this.createExtensions,
        create_ivrs: this.createIvrs,
        update_ivr_profiles: undefined,
        create_faxes: this.createFaxes,
        create_gateways: this.createGateways,
        create_gateway_routing: this.createGatewayRoutings,
        create_groups: this.createGroups,
        add_domain_admins: this.importDomainAdmins,
        domain_address_book: this.importDomainAddressBook,
    };
}
